/**
 * Link in Bio — Learn-Mode inline tips.
 *
 * Anchored across `routes/social/LinkInBio.tsx`, `routes/social/LinkInBioEditor.tsx`
 * and `components/link-in-bio/*`.
 */

import type { LearnTip } from "./types";

export const linkInBioTips: LearnTip[] = [
  {
    key: "social-link-in-bio",
    title: "Link in Bio",
    body: "One branded page per client to put in their Instagram or TikTok bio. It lists their key links, shows their Instagram grid and counts every visit and click.",
  },
  {
    key: "social-link-in-bio-create",
    title: "New Page",
    body: "Creates a draft for the selected client using their brand kit: colors, fonts, logo and their connected Instagram account. Nothing is public until you publish.",
  },
  {
    key: "social-link-in-bio-editor",
    title: "Page Editor",
    body: "Edit the blocks, look and Instagram grid on the left; the phone on the right shows what visitors see right now.",
  },
  {
    key: "social-link-in-bio-publish",
    title: "Publish and Share",
    body: "Publish makes the page live at the address shown. Copy that address into the client's profile bio. Unpublish or Disable takes it offline immediately.",
  },
  {
    key: "social-link-in-bio-blocks",
    title: "Blocks",
    body: "Buttons link out; headers and text group and explain them; social profiles show as small chips at the top.",
  },
  {
    key: "social-link-in-bio-reorder",
    title: "Reorder Blocks",
    body: "Drag a block by its handle, or use the up and down arrows. The new order saves right away.",
  },
  {
    key: "social-link-in-bio-block-form",
    title: "Block Details",
    body: "Only full http:// or https:// links are accepted. A thumbnail image is optional and must be an https:// image link.",
  },
  {
    key: "social-link-in-bio-schedule",
    title: "Link Scheduling",
    body: "Set when a link appears and when it disappears, for example a sale or event. Outside that window the link is hidden automatically.",
  },
  {
    key: "social-link-in-bio-settings",
    title: "Page Settings",
    body: "Address, title, bio, colors, tracking tags and search visibility for this page.",
  },
  {
    key: "social-link-in-bio-address",
    title: "Page Address",
    body: "The last part of the public link. Changing it breaks any link already shared, so set it once.",
  },
  {
    key: "social-link-in-bio-theme",
    title: "Page Look",
    body: "Starts from the client's brand kit. Dark mode can follow the visitor's phone setting or keep these colors.",
  },
  {
    key: "social-link-in-bio-utm",
    title: "Tracking Tags",
    body: "Adds UTM tags to outgoing links so the client's analytics credits Instagram, TikTok or Facebook. Links that already carry UTM tags are left alone.",
    bullets: [
      "utm_source: where the visitor came from",
      "utm_medium: social",
      "utm_campaign: this page's campaign",
      "utm_content: the link's label",
    ],
  },
  {
    key: "social-link-in-bio-grid",
    title: "Instagram Grid",
    body: "Shows the client's latest Instagram feed posts (never Stories). Tap a post to give it up to five links or hide it.",
  },
  {
    key: "social-link-in-bio-post-links",
    title: "Post Links",
    body: "Visitors who tap this post go straight to its link, or pick from a short list when there are several.",
  },
  {
    key: "social-link-in-bio-analytics",
    title: "Page Analytics",
    body: "Views, clicks, daily unique visitors and click-through rate (clicks divided by views), by day, link or source. Bots and link previews are left out.",
  },
  {
    key: "social-link-in-bio-preview",
    title: "Phone Preview",
    body: "A close preview of the live page. Scheduled links appear here only while they are inside their window.",
  },
];
